<!--
  Stripe payment iframe component
  
  2025-06-24: Made text fields more rounded by increasing border-radius
-->

<template>
  <v-card elevation="0" class="px-5 py-8" width="400">
    <div class="stripe-form">
      <v-row class="d-flex mb-4">
        <v-col class="d-flex pt-0 flex-column stripe-field-wrapper" cols="12">
          <div id="card-number-label" class="field-label">Card Number</div>
          <div
            ref="card"
            class="field flex"
            aria-labelledby="card-number-label"
          >
            <!-- A Stripe Element will be inserted here. -->
          </div>
          <div v-if="errors.cardNumber" class="text-caption error--text ml-2">
            {{ errors.cardNumber }}
          </div>
        </v-col>
        <v-col cols="6" class="d-flex flex-column stripe-field-wrapper">
          <div id="expiry-date-label" class="field-label">Expiry Date</div>
          <div ref="exp" class="field flex" aria-labelledby="expiry-date-label">
            <!-- A Stripe Element will be inserted here. -->
          </div>
          <div v-if="errors.cardExpiry" class="text-caption error--text ml-2">
            {{ errors.cardExpiry }}
          </div>
        </v-col>
        <v-col cols="6" class="d-flex flex-column stripe-field-wrapper">
          <div id="cvv-label" class="field-label">CVV</div>
          <div ref="cvc" class="field flex" aria-labelledby="cvv-label">
            <!-- A Stripe Element will be inserted here. -->
          </div>
          <div v-if="errors.cardCvc" class="text-caption error--text ml-2">
            {{ errors.cardCvc }}
          </div>
        </v-col>
      </v-row>
      <v-divider
        v-if="cardErrors"
        :class="[
          'form-divider',
          'mt-2',
          { 'card-field-style': cardErrors, 'card-focus': cardFocus },
        ]"
      />
      <div class="text-caption error--text">
        {{ cardErrors }}
      </div>
      <slot name="disclaimer" />
      <div class="text-right">
        <v-btn
          :loading="processing"
          color="primary lighten-1"
          class="font-weight-bold rounded-lg"
          :style="
            customButtonColor
              ? `background-color: #${customButtonColor} !important; color: white !important;`
              : ''
          "
          elevation="0"
          block
          large
          @click.prevent="onSubmit"
        >
          {{ buttonText }}
        </v-btn>
      </div>

      <!-- Stripe Footer -->
      <div class="stripe-footer mt-6">
        <div class="secure-payment-text">
          <v-icon small class="mr-1" color="grey">mdi-lock</v-icon>
          <span class="text-caption grey--text"
            >Secure 3D payment powered by</span
          >
          <span class="stripe-logo ml-1">stripe</span>
        </div>

        <div class="payment-cards mt-3">
          <div class="card-logos">
            <!-- Visa -->
            <div class="card-logo visa">VISA</div>
            <!-- Mastercard -->
            <div class="card-logo mastercard">
              <div class="mc-circle red"></div>
              <div class="mc-circle orange"></div>
            </div>
            <!-- American Express -->
            <div class="card-logo amex">AMERICAN<br />EXPRESS</div>
            <!-- Klarna -->
            <div class="card-logo klarna">Klarna.</div>
          </div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import formRules from 'components/mixins/form-rules-mixin'
import { loadStripe } from '@stripe/stripe-js'
import axios from 'axios'

const elementStyles = {
  base: {
    color: '#000000',
    fontWeight: 500,
    fontFamily: 'Montserrat',
    fontSize: '15px',
    borderColor: 'rgba(0, 0, 0, 0.2)',

    ':focus': {
      color: '#000000',
    },

    '::placeholder': {
      color: 'rgba(0, 0, 0, .6)',
    },

    ':focus::placeholder': {
      color: '#CFD7DF',
    },
  },
  invalid: {
    color: '#A01622',
    '::placeholder': {
      color: '#FFCCA5',
    },
  },
}

const elementClasses = {
  focus: 'focus',
  empty: 'empty',
  invalid: 'invalid',
}

const elementsOptions = {
  fonts: [
    {
      cssSrc:
        'https://fonts.googleapis.com/css?family=Montserrat:300,400,500,700',
    },
  ],
}

export default {
  name: 'StripeElement',
  mixins: [formRules],
  props: {
    buttonText: {
      type: String,
      default: 'Confirm & Pay',
    },
  },
  data() {
    return {
      stripe: undefined,
      card: undefined,
      cvc: undefined,
      exp: undefined,
      errors: {
        cardNumber: '',
        cardExpiry: '',
        cardCvc: '',
      },
      cardErrors: '',
      valid: false,
      cardFocus: false,
      processing: false,
    }
  },
  computed: {
    publicKey() {
      return this.$route.query.publicKey
    },
    clientSecret() {
      return this.$route.query.clientSecret
    },
    paymentIntent() {
      return this.$route.query.paymentIntent
    },
    customButtonColor() {
      return this.$route.query.buttonColor
        ? decodeURIComponent(this.$route.query.buttonColor)
        : null
    },
  },
  mounted: async function () {
    if (!this.publicKey) {
      console.error('Stripe public key is required')
      return
    }
    this.stripe = await loadStripe(this.publicKey)
    const stripeElements = this.stripe.elements(elementsOptions)

    this.card = stripeElements.create('cardNumber', {
      showIcon: true,
      hidePostalCode: true,
      style: elementStyles,
      classes: elementClasses,
      placeholder: 'Card Number',
    })
    this.cvc = stripeElements.create('cardCvc', {
      style: elementStyles,
      classes: elementClasses,
    })
    this.exp = stripeElements.create('cardExpiry', {
      style: elementStyles,
      classes: elementClasses,
    })
    this.card.mount(this.$refs.card)
    this.exp.mount(this.$refs.exp)
    this.cvc.mount(this.$refs.cvc)
    this.card.on('change', e => {
      if (e.error) {
        this.errors.cardNumber = e.error.message
      } else {
        this.errors.cardNumber = ''
      }
    })
    this.exp.on('change', e => {
      if (e.error) {
        this.errors.cardExpiry = e.error.message
      } else {
        this.errors.cardExpiry = ''
      }
    })
    this.cvc.on('change', e => {
      if (e.error) {
        this.errors.cardCvc = e.error.message
      } else {
        this.errors.cardCvc = ''
      }
    })
    this.card.on('focus', () => {
      this.cardFocus = true
    })
  },
  methods: {
    async onSubmit() {
      this.processing = true
      this.$emit('setup-processing')

      try {
        const { setupIntent, error } = await this.stripe.confirmCardSetup(
          this.clientSecret,
          { payment_method: { card: this.card } }
        )

        if (error) {
          this.cardErrors = error.message
          window.parent.postMessage(
            { status: 'error', message: error.message },
            '*'
          )
          this.$emit('setup-failed', error)
          return
        }

        this.cardErrors = ''

        const res = await axios.post('/api/stripe/purchase', {
          payment_intent_id: this.paymentIntent,
          amount: setupIntent.amount,
          payment_method: setupIntent.payment_method,
        })
        if (res.status === 200) {
          this.$emit('setup-success', {
            setupIntent,
            paymentMethod: setupIntent.payment_method,
          })
          window.parent.postMessage({ status: 'success', data: res.data }, '*')
        }
        this.$emit('setup-success', {
          setupIntent,
          paymentMethod: setupIntent.payment_method,
        })
      } catch (error) {
        window.parent.postMessage(
          { status: 'error', message: error.message },
          '*'
        )
        this.cardErrors = error.message
        this.$emit('setup-failed', error)
      } finally {
        this.processing = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.field-label {
  font-size: 15px;
  font-weight: 600;
  color: #333333;
  margin-bottom: 6px;
  font-family: 'Montserrat', sans-serif;
}

label {
  position: relative;
  color: #6a7c94;
  font-weight: 400;
  height: 32px;
  line-height: 32px;
  margin-bottom: 10px;
  display: flex;
  flex-direction: row;
}

label > span {
  width: 115px;
}

.form-divider {
  background: rgb(118, 118, 118);
}
.card-focus:not(.card-field-style) {
  background: #7ebac0;
  height: 2px;
}

.stripe-link {
  text-decoration: none;
}
.stripe-badge {
  line-height: 0;
}

.stripe-form .field {
  padding: 10px 12px 10px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  width: 100%;
  margin-top: 6px;
  + div {
    position: absolute;
    left: 15px;
    bottom: -10px;
  }
}

.stripe-form .field.focus,
.stripe-form .field:focus {
  border-color: #666666;
  border-width: 2px;
}

.stripe-form .field.invalid {
  border-color: var(--v-error-base);
  color: var(--v-error-base);
  border-width: 2px;
}

.stripe-form input {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  outline: none;
  border-style: none;
}

.stripe-field-wrapper {
  position: relative;
}

.stripe-footer {
  text-align: center;
}

.secure-payment-text {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

.stripe-logo {
  color: #635bff;
  font-weight: 600;
  font-size: 14px;
}

.payment-cards {
  display: flex;
  justify-content: center;
}

.card-logos {
  display: flex;
  gap: 12px;
  align-items: center;
}

.card-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 4px 8px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 10px;
  background: white;
  border: 1px solid #e0e0e0;
}

.card-logo.visa {
  color: #1a1f71;
  background: #ffffff;
  font-size: 12px;
  font-weight: bold;
  min-width: 50px;
}

.card-logo.mastercard {
  position: relative;
  min-width: 50px;
  height: 32px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mc-circle {
  position: absolute;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  top: 50%;
  transform: translateY(-50%);
}

.mc-circle.red {
  background: #eb001b;
  left: 12px;
}

.mc-circle.orange {
  background: #ff5f00;
  left: 20px;
}

.card-logo.amex {
  color: #006fcf;
  background: #ffffff;
  font-size: 8px;
  font-weight: bold;
  min-width: 50px;
  line-height: 1.1;
}

.card-logo.klarna {
  background: #ffb3c7;
  color: #000;
  font-size: 12px;
  font-weight: bold;
  min-width: 50px;
  border-radius: 8px;
}
</style>
